<template>
  <b-button v-b-modal.modal-load>
    <b-icon-cloud-download aria-hidden="true"></b-icon-cloud-download>
    Load
    <b-modal
        id="modal-load"
        title="Load Design"
        @ok="useSelectedDesign"
        :okDisabled="!designToSelect">

      <div class="container">
        <div v-if="error">
          <p><em>An error occurred in loading designs:</em></p>
          <p>{{ errorMessage }}</p>
          <p>
            <b-button @click="netLoadDesigns(true)">
              <b-icon-arrow-counterclockwise aria-hidden="true"></b-icon-arrow-counterclockwise>
              Retry
            </b-button>
            or try again later.
          </p>
        </div>

        <div v-if="!error && (!savedDesigns || savedDesigns.length === 0)">
          <p>No previously uploaded designs.</p>
          <p v-if="!config.user">
            If you have an account,
            <a :href="config.loginURL">sign in</a> to access existing saved designs.
          </p>
        </div>
        <DesignThumb
            v-for="d in savedDesigns" :key="d.key"
            :design="d"
            :width="115">
        </DesignThumb>
      </div>
      <template v-slot:modal-ok>
          <b-icon-cloud-download aria-hidden="true"></b-icon-cloud-download>
          Load Design
      </template>
    </b-modal>
  </b-button>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import axios from 'axios';

import { BIconCloudDownload, BIconArrowCounterclockwise } from 'bootstrap-vue';

import DesignThumb from '@/components/DesignThumb.vue';
import { copy } from '@/utils';

export default {
  name: 'DesignLoad',
  components: {
    DesignThumb,
    BIconCloudDownload,
    BIconArrowCounterclockwise,
  },
  props: {
    // msg: String,
    loadImmediate: String,
  },
  data() {
    return {
      designs: null, // null for needs loading; array after loading
      error: null,
    };
  },
  computed: {
    errorMessage() {
      let errmsg = 'An unknown error occurred.';
      if (this.error.request) {
        console.log(this.error.request);
        errmsg = `${this.error.response.statusText} (${this.error.response.status})`;
      } else if (this.error.response) {
        console.log(this.error.response);
        errmsg = 'Could not contact the server.';
      } else {
        console.log(this.error.message);
        errmsg = `An unknown error occurred: ${this.error.message}`;
      }
      return errmsg;
    },
    ...mapState(['config', 'product', 'products', 'leadProduct', 'savedDesigns', 'designToSelect']),
    ...mapGetters(['skuMap']),
  },
  methods: {
    convertServerDesignInfo(serverJsonDesign, expandLinked = true) {
      const design = {
        name: serverJsonDesign.name,
        key: serverJsonDesign.key,
        sku: serverJsonDesign.product,
        designcode: serverJsonDesign.key,
        image: serverJsonDesign.image,
        color: serverJsonDesign.color,
        relativePosition: {
          centerX: serverJsonDesign.centerx,
          centerY: serverJsonDesign.centery,
          width: serverJsonDesign.relativew,
          height: serverJsonDesign.relativeh,
          rot: serverJsonDesign.rotation,
        },
        toolimages: serverJsonDesign.toolimages,
        linkdesign: serverJsonDesign.linkdesign,
      };
      if (design.linkdesign && expandLinked) {
        design.linkdesign = this.convertServerDesignInfo(design.linkdesign, false);
      }
      return design;
    },
    netLoadDesign(designcode) {
      // used for immediate load
      console.log('netLoadDesign', this.config.designLoadURL + designcode);
      axios.get(this.config.designLoadURL + designcode)
        .then((response) => {
          // console.log('netLoadDesign data:', response.data);
          if (response.data) {
            const design = this.convertServerDesignInfo(response.data);
            // console.log('netLoadDesign', 'converted data:', design);
            this.applyDesign(design);
          }
        })
        .catch((error) => {
          console.log(error);
          let errmsg = 'An unknown error occurred.';
          if (error.request) {
            console.log(error.request);
            errmsg = `An error occurred in loading design: ${error.response.statusText} (${error.response.status})`;
          } else if (error.response) {
            console.log(error.response);
            errmsg = 'Could not contact the server to load design.';
          } else {
            console.log(error.message);
            errmsg = `An unknown error occurred in loading design: ${error.message}`;
          }
          this.$bvToast.toast(
            errmsg,
            {
              title: 'Design Load Error',
              toaster: 'b-toaster-top-center',
              autoHideDelay: 12000,
              variant: 'danger',
              solid: true,
            },
          );
        });
    },
    netLoadDesigns(toast = false) {
      const loadProduct = this.products[this.leadProduct].id;
      console.log('netLoadDesigns', this.config.designsListURL + loadProduct);
      axios.get(this.config.designsListURL + loadProduct)
        .then((response) => {
          const designs = [];
          // console.log('netLoadDesigns raw data:', response.data);
          response.data.forEach((design) => {
            designs.push(this.convertServerDesignInfo(design));
          });
          this.$store.commit('setSavedDesigns', designs);
          this.error = null;
          console.log('loaded designs:', this.savedDesigns);
        })
        .catch((error) => {
          console.log(error);
          this.error = error;
          if (toast) {
            this.$bvToast.toast(
              this.errorMessage,
              {
                title: 'Load Designs Error',
                toaster: 'b-toaster-top-center',
                autoHideDelay: 12000,
                variant: 'danger',
                solid: true,
              },
            );
          }
        });
    },
    reloadDesigns() {
      console.log('reloadDesigns (saveDesigns watch triggered)', this.savedDesigns);
      if (this.savedDesigns === null) {
        this.$nextTick(() => { // allow time for layout to settle
          setTimeout(this.netLoadDesigns, 1000);
        });
      }
    },
    useSelectedDesign() {
      console.log('useSelectedDesign');
      const selectList = this.savedDesigns.filter(
        (elt) => elt.key === this.designToSelect,
      );
      if (selectList.length > 0) {
        const selected = copy(selectList[0]);
        delete selected.toolimages;
        this.applyDesign(selected);
        this.$store.commit('clearDesignToSelect');
      }
    },
    /* Return a list of 'choices', objects from a product's 'options' */
    validOptionChoices(option, includeOutOfStock = false) {
      // console.log('validOptionChoices', 'w/oos:', includeOutOfStock, option);
      const options = this.product.options.filter((choice) => choice.name === option);
      const chosenOption = options.length > 0 ? options[0] : null;
      let choices = [];
      if (chosenOption) {
        choices = chosenOption.choices
          .filter((choice) => choice.name)
          .filter((choice) => !choice.hidden || includeOutOfStock)
          .filter((choice) => !choice.outofstock || includeOutOfStock);
      }
      // console.log('validOptionChoices => ', choices);
      return choices;
    },
    /* Load the design-info-containing object 'design' as current design.
     * May modify design if parts are wrong or options unavailable, so make
     * a copy before passing it in if you care. */
    applyDesign(design) {
      // need to handle unavailable colors...
      let newcolor = design.color;
      let notify = '';
      newcolor = design.color;

      const optionChoices = this.validOptionChoices('color');
      const optionNames = optionChoices.map((choice) => choice.name);
      if (optionChoices.length > 0 && !optionNames.includes(design.color)) {
        // design color is not in available choices

        // prep data we (may/will) need later
        const defaultColor = optionChoices[0].name;
        const optionChoicesOOS = this.validOptionChoices('color', true);
        const optionNamesOOS = optionChoicesOOS.map((choice) => choice.name);
        const oxycolor = `${design.color}oxy`;

        if (optionNames.includes(oxycolor)) {
          // there's a corresponding oxy color
          console.log('option fallback: oxy');
          notify = 'Please note: This color of cap has been upgraded to oxygen scavenging, and may have a slightly different color.';
          newcolor = oxycolor;
        } else if (optionNamesOOS.includes(design.color)) {
          // the design color is present if we include out of stock choices
          const oosAlternate = optionChoicesOOS.filter(
            (choice) => choice.name === design.color,
          )[0].outofstock;

          if (optionNames.includes(oosAlternate)) {
            // a direct alternate is specified in the choice's 'outofstock' attribute
            console.log('option fallback: oos specified alternative', oosAlternate);
            notify = 'The base product used in this design is out of stock. Check out this alternate product.';
            newcolor = oosAlternate;
          } else {
            // no alternative is specified, use default
            console.log('option fallback: oos default');
            notify = 'The base product used in this design is not available. Sorry! Try on some others and see how they look.';
            newcolor = defaultColor;
          }
        } else {
          // choice is missing some other way
          console.log('option fallback: fallthrough default');
          notify = 'The base product used in this design is not available. Sorry! Try on some others and see how they look.';
          newcolor = defaultColor;
        }
      }
      if (notify) {
        console.log('option unavailable notification:', notify);
        console.log('option color changed to', newcolor);
        this.$bvToast.toast(
          notify,
          {
            title: 'Product option unavailable',
            toaster: 'b-toaster-bottom-center',
            autoHideDelay: 12000,
            variant: 'primary',
            solid: true,
          },
        );
      }
      this.$store.commit('setSpecificDesign', {
        designparams: design,
        designkey: this.skuMap[design.sku],
      });

      this.$store.commit('setProductOptions', { color: newcolor });
      if (design.linkdesign) {
        // we simplfy loading the second design a bit; it's currently only neckers, so this is okay.
        this.$store.commit('setSpecificDesign', {
          designparams: design.linkdesign,
          designkey: this.skuMap[design.linkdesign.sku],
        });
      }
      this.$store.commit('updateLastSavedDesigns');
      this.$emit('designLoaded');
    },
  },
  mounted() {
    this.netLoadDesigns();
    if (this.loadImmediate) {
      console.log('load design immediate:', this.loadImmediate);
      this.netLoadDesign(this.loadImmediate);
    }
  },
  watch: {
    savedDesigns: 'reloadDesigns',
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.container {
  height: 55vh;
  overflow-y: scroll;
}
</style>
